'use client';

import { FC, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  FeedCommentContent,
  FeedEntry,
  FeedPostContent,
  FeedPaperContent,
  FeedGrantContent,
} from '@/types/feed';
import { FeedItemFundraise } from './items/FeedItemFundraise';
import { FeedItemPaper } from './items/FeedItemPaper';
import { FeedItemComment } from './items/FeedItemComment';
import { FeedItemPost } from './items/FeedItemPost';
import { FeedItemGrant } from './items/FeedItemGrant';
import { FeedItemGrantWithApplicants } from './items/FeedItemGrantWithApplicants';
import { useFeedItemAnalyticsTracking } from '@/hooks/useFeedItemAnalyticsTracking';
import { getUnifiedDocumentId } from '@/types/analytics';
import { FeedItemBountyComment } from './items/FeedItemBountyComment';
import { FeedItemBounty } from './items/FeedItemBounty';
import { buildWorkUrl } from '@/utils/url';
import { ContentType } from '@/types/work';

export interface Highlight {
  field: string;
  value: string;
}

interface FeedEntryItemProps {
  entry: FeedEntry;
  index: number;
  showBountyFooter?: boolean;
  hideActions?: boolean;
  maxLength?: number;
  showGrantHeaders?: boolean;
  showFundraiseHeaders?: boolean;
  showPostHeaders?: boolean;
  showReadMoreCTA?: boolean;
  feedOrdering?: string;
  registerVisibleItem: (index: number, unifiedDocumentId: string) => void;
  unregisterVisibleItem: (index: number, unifiedDocumentId: string) => void;
  getVisibleItems: (clickedUnifiedDocumentId: string) => string[];
  shouldRenderBountyAsComment?: boolean;
  highlights?: Highlight[];
  showBountyInfo?: boolean;
  abstractCollapsedByDefault?: boolean;
}

export const FeedEntryItem: FC<FeedEntryItemProps> = ({
  showBountyInfo,
  entry,
  index,
  showBountyFooter = true,
  hideActions = false,
  maxLength,
  showGrantHeaders = true,
  showFundraiseHeaders = true,
  showPostHeaders = true,
  showReadMoreCTA = false,
  feedOrdering,
  registerVisibleItem,
  unregisterVisibleItem,
  getVisibleItems,
  shouldRenderBountyAsComment = false,
  highlights,
  abstractCollapsedByDefault,
}) => {
  const unifiedDocumentId = getUnifiedDocumentId(entry);

  const { ref } = useInView({
    threshold: 0,
    rootMargin: '50px',
    onChange: (inView) => {
      if (unifiedDocumentId) {
        if (inView) {
          registerVisibleItem(index, unifiedDocumentId);
        } else {
          unregisterVisibleItem(index, unifiedDocumentId);
        }
      }
    },
  });

  const getImpressions = useCallback(() => {
    if (!unifiedDocumentId) return undefined;
    const visibleItems = getVisibleItems(unifiedDocumentId);

    return visibleItems.length > 0 ? visibleItems : undefined;
  }, [unifiedDocumentId, getVisibleItems]);

  // Handle feed item interactions with analytics
  const { handleFeedItemClick, handleAbstractExpanded } = useFeedItemAnalyticsTracking({
    entry,
    feedPosition: index + 1,
    feedOrdering,
    impression: getImpressions(),
  });

  if (!entry) {
    console.error('Feed entry is undefined');
    return null;
  }

  // Apply appropriate spacing based on position
  const spacingClass = index !== 0 ? 'mt-8' : '';

  // Generate the appropriate href for this entry
  const generateHref = (entry: FeedEntry): string | undefined => {
    try {
      switch (entry.contentType) {
        case 'POST': {
          const postContent = entry.content as FeedPostContent;
          return buildWorkUrl({
            id: postContent.id,
            slug: postContent.slug,
            contentType: postContent.postType === 'QUESTION' ? 'question' : 'post',
          });
        }
        case 'PREREGISTRATION': {
          const fundContent = entry.content as FeedPostContent;
          return buildWorkUrl({
            id: fundContent.id,
            slug: fundContent.slug,
            contentType: 'preregistration',
          });
        }
        case 'PAPER': {
          const paperContent = entry.content as FeedPaperContent;
          return buildWorkUrl({
            id: paperContent.id,
            slug: paperContent.slug,
            contentType: 'paper',
          });
        }

        case 'BOUNTY':
          if (entry.relatedWork) {
            let contentType = entry.relatedWork.contentType;
            if (
              'postType' in entry.relatedWork &&
              entry.relatedWork.postType === 'QUESTION' &&
              contentType === 'post'
            ) {
              contentType = 'question';
            }

            return buildWorkUrl({
              id: entry.relatedWork.id,
              slug: entry.relatedWork.slug,
              contentType: contentType,
              tab: 'bounties',
            });
          }
          break;

        case 'COMMENT': {
          const comment = entry.content as FeedCommentContent;
          if (entry.relatedWork) {
            let contentType = entry.relatedWork.contentType;
            if (
              'postType' in entry.relatedWork &&
              entry.relatedWork.postType === 'QUESTION' &&
              contentType === 'post'
            ) {
              contentType = 'question';
            }

            const baseUrl = buildWorkUrl({
              id: entry.relatedWork.id,
              slug: entry.relatedWork.slug,
              contentType: contentType,
              tab: 'conversation',
            });
            return `${baseUrl}#comment-${comment.id}`;
          }
          break;
        }

        case 'GRANT': {
          const grantContent = entry.content as FeedGrantContent;
          return buildWorkUrl({
            id: grantContent.id,
            slug: grantContent.slug,
            contentType: 'funding_request',
          });
        }

        default:
          return undefined;
      }
    } catch (error) {
      console.error('Error generating href for entry:', error, entry);
      return undefined;
    }
  };

  const href = generateHref(entry);

  let content = null;

  const openBounties = entry.content.bounties?.filter((b) => b.status === 'OPEN') ?? [];
  const shouldRenderAsBountyCard = showBountyInfo && openBounties.length > 0;

  try {
    // Use the contentType field on the FeedEntry object to determine the type of content
    // When showBountyInfo is set and the entry has open bounties, render as a uniform bounty card
    if (
      shouldRenderAsBountyCard &&
      (entry.contentType === 'POST' ||
        entry.contentType === 'PAPER' ||
        entry.contentType === 'PREREGISTRATION')
    ) {
      content = (
        <FeedItemBounty
          entry={entry}
          href={href}
          showActions={!hideActions}
          maxLength={maxLength}
          onFeedItemClick={handleFeedItemClick}
        />
      );
    } else
      switch (entry.contentType) {
        case 'POST':
          content = (
            <FeedItemPost
              entry={entry}
              showHeader={showPostHeaders}
              href={href}
              showActions={!hideActions}
              maxLength={maxLength}
              onFeedItemClick={handleFeedItemClick}
              onAbstractExpanded={handleAbstractExpanded}
              highlights={highlights}
              showBountyInfo={showBountyInfo}
            />
          );
          break;

        case 'PREREGISTRATION':
          content = (
            <FeedItemFundraise
              entry={entry}
              href={href}
              showHeader={showFundraiseHeaders}
              showActions={!hideActions}
              maxLength={maxLength}
              onFeedItemClick={handleFeedItemClick}
              showBountyInfo={showBountyInfo}
            />
          );
          break;

        case 'PAPER':
          content = (
            <FeedItemPaper
              entry={entry}
              href={href}
              showActions={!hideActions}
              maxLength={maxLength}
              onFeedItemClick={handleFeedItemClick}
              onAbstractExpanded={handleAbstractExpanded}
              highlights={highlights}
              showBountyInfo={showBountyInfo}
              abstractCollapsedByDefault={abstractCollapsedByDefault}
            />
          );
          break;

        case 'BOUNTY': {
          if (shouldRenderBountyAsComment) {
            content = (
              <FeedItemBountyComment
                entry={entry}
                relatedDocumentId={entry.relatedWork?.id}
                href={href}
                showContributeButton={false}
                showFooter={showBountyFooter}
                showSupportAndCTAButtons={false}
                showDeadline={false}
                maxLength={maxLength}
                onFeedItemClick={handleFeedItemClick}
              />
            );
          } else {
            content = (
              <FeedItemBounty
                entry={entry}
                href={href}
                showActions={showBountyFooter}
                maxLength={maxLength}
                onFeedItemClick={handleFeedItemClick}
              />
            );
          }
          break;
        }

        case 'COMMENT':
          // Use FeedItemComment for comment entries
          content = (
            <FeedItemComment
              showReadMoreCTA={showReadMoreCTA}
              entry={entry}
              href={href}
              showCreatorActions={true}
              workContentType={entry.relatedWork?.contentType}
              hideActions={hideActions}
              maxLength={maxLength}
              onFeedItemClick={handleFeedItemClick}
            />
          );
          break;

        case 'GRANT':
          content = <FeedItemGrantWithApplicants entry={entry} />;
          break;

        default:
          throw new Error(`Unsupported content type: ${entry.contentType}`);
      }
  } catch (error) {}

  return (
    <div ref={ref} className={spacingClass}>
      {content}
    </div>
  );
};
